import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ModalBackdrop } from "./ModalBackdrop";
import { useEscapeKey } from "./useEscapeKey";
import { useSavedShelf } from "./useSavedShelf";
import { SaveToRoomButton } from "./SaveToRoomButton";
import BookmarkControls from "./BookmarkControls";
import { toImagePage } from "../lib/bookmarks";
import { type ShelfBook, type FeaturedBook, shelfSlug, fitSpine } from "../lib/shelves";
import { useShelfOverflow } from "../lib/shelf-overflow";
import { t } from "../strings.js";

// ROADMAP ITEM 7 — **THE BOOK READER AND THE BOOK SHELF COME OUT OF `App.tsx`, IN THE SAME AREA AND
// BY THE SAME SEAM AS `VideoShelf.tsx`.** (2026-09-04)
//
// Her standing law: *"refactor `src/App.tsx` into components OPPORTUNISTICALLY as areas are touched
// — no big-bang rewrite."* The video half of this surface left one window ago; these two are its
// pair, and `LibraryPage` is the only caller of either.
//
// **TWO COMPONENTS AND NOTHING ELSE MOVED. THEIR BODIES ARE UNCHANGED, COMMENTS AND ALL** — an
// extraction that also edits is two changes wearing one diff, and the second one is the one nobody
// reviews. That is `VideoShelf.tsx`'s own rule, kept.
//
// **NOTHING HAD TO BE WIDENED TO LET THEM LEAVE**, which is the whole test for whether an extraction
// is the cheap one: `useSavedShelf`, `SaveToRoomButton`, `BookmarkControls`, `ModalBackdrop` and
// `useEscapeKey` are already components in this directory, and the types, `shelfSlug`,
// `useShelfOverflow`, `toImagePage` and `t` are laws `App.tsx` was already importing. Not one export
// was added to `App.tsx` to make this possible.

// Page-image reader: the designed pages of a featured book, with mid-page arrows
// (always visible without scrolling), a page counter, and close. Nav-safe modal.
export function PageImageReader({ book, onClose, startPage = 1, language }: { book: FeaturedBook; onClose: () => void; startPage?: number; language: string }) {
  const [page, setPage] = useState(Math.max(1, startPage));
  const shelf = useSavedShelf();   // W5-2: her reader saves to My Room like every other reader
  const total = book.pageCount || 1;
  const go = (delta: number) => setPage((p) => Math.min(total, Math.max(1, p + delta)));

  // R82: the page turns keep their own handler (they need `total`); the CLOSE is the house's one
  // law. Splitting them is the fix as well as the fold — this effect depended on `[total]` alone,
  // so Escape here was calling whichever `onClose` this reader was handed on its first render.
  useEscapeKey(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total]);

  return (
    <ModalBackdrop label={t(language, "reader.modalLabel", { title: book.title })} className="page-reader-backdrop">
      <div className="page-reader" onCopy={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()}>
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "reader.close")}>x</button>
        <header className="page-reader__head">
          <h2 className="page-reader__title">{book.title}</h2>
          {book.author ? <p className="page-reader__author">{book.author}</p> : null}
          <SaveToRoomButton slug={book.slug} title={book.title} saved={shelf.isSaved(book.slug)} onToggle={shelf.toggle} language={language} />
        </header>
        <div className="page-reader__stage">
          <button className="page-reader__arrow page-reader__arrow--prev" type="button" onClick={() => go(-1)} disabled={page <= 1} aria-label={t(language, "reader.prevPage")}>
            <ArrowLeft size={22} />
          </button>
          <img
            className="page-reader__page"
            src={`/api/library/page/${book.slug}/${page}`}
            alt={t(language, "reader.pageAlt", { title: book.title, page })}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
          <button className="page-reader__arrow page-reader__arrow--next" type="button" onClick={() => go(1)} disabled={page >= total} aria-label={t(language, "reader.nextPage")}>
            <ArrowRight size={22} />
          </button>
        </div>
        <div className="page-reader__foot">
          <button type="button" onClick={() => go(-1)} disabled={page <= 1}>{t(language, "reader.prev")}</button>
          <span className="page-reader__count">{t(language, "reader.pageOf", { page, total })}</span>
          <button type="button" onClick={() => go(1)} disabled={page >= total}>{t(language, "reader.next")}</button>
        </div>
        <BookmarkControls bookSlug={book.slug} bookTitle={book.title} page={page} onJump={(p) => setPage(toImagePage(p, total))} language={language} />
      </div>
    </ModalBackdrop>
  );
}

// One category's floating shelf: a VERTICAL gold-chrome category label on the left,
// then a row of black-and-gold spines resting on a floating gold plank (no bookcase,
// no back panel, no sides — the page background shows through everywhere else).
// 14-B7d(3) — ONE ESCAPE LAW, AND THE COUNT IS THE POINT: this exact effect was typed EIGHT times,
// byte for byte, across this file. R82 moved the hook itself to `src/components/useEscapeKey.ts`,
// because a component that leaves App.tsx cannot import back into it — which is precisely why two
// more hand-typed copies were sitting out there. It is imported at the top of this file now.

// 14-B7d(2) — ONE SLIDE LAW FOR BOTH SHELVES, DERIVED RATHER THAN TYPED TWICE (R31's currency).
// A shelf shows a slide arrow at an end only when it actually overflows in that direction, so a
// short shelf shows none. The books shelf and the videos shelf carried the same twenty lines with
// one number different, and the two copies had already stopped being obviously the same thing: the
// only real difference is the minimum a tap slides by, so that is the parameter.
// 14-B7d(1) — `category` and `label` ARE TWO DIFFERENT FACTS AND THEY USED TO BE ONE STRING.
// `category` is the DATA: the id through `shelfSlug`, what the filter chips scroll to, and the key
// the catalogue itself is grouped by. `label` is what is PAINTED on the plate. In the Library they
// are the same (the plate is the catalogue's own word for that shelf, which is her scope question
// along with the book titles); in My Room the plate is the house's own word and turns.
export function Shelf({
  category,
  label,
  books,
  activeSlug,
  onOpen,
  isSaved,
  onSave,
  language,
}: {
  category: string;
  label?: string;
  books: ShelfBook[];
  activeSlug: string | null;
  onOpen: (book: ShelfBook) => void;
  // W5-2: Save to My Room on every book's shelf view. Omitted on the SAVED shelf in My Room, which
  // is the far end of this loop rather than a browse view — everything on it is already saved.
  isSaved?: (slug: string) => boolean | null;
  onSave?: (slug: string) => void;
  language: string;
}) {
  const plate = label || category;
  const { booksRef, overflow, slide } = useShelfOverflow(books.length, 240);

  return (
    <section id={shelfSlug(category)} className="shelf-row" aria-label={t(language, "library.shelf.books", { name: plate })}>
      <div className="shelf-row__label">
        <span>{plate}</span>
      </div>
      <div className="shelf-row__unit">
        <div className="shelf-row__scroller">
          <button
            type="button"
            className={`shelf-arrow shelf-arrow--left${overflow.left ? "" : " shelf-arrow--off"}`}
            onClick={() => slide(-1)}
            aria-label={t(language, "library.shelf.slideLeft", { name: plate })}
            tabIndex={overflow.left ? 0 : -1}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="shelf-row__books" ref={booksRef}>
            {books.map((book) => {
              const spine = fitSpine(book.title);
              return (
                <div
                  key={book.slug}
                  className="lib-book-slot"
                  style={{ "--spine-h": `${spine.height}px`, "--spine-w": `${spine.width}px` } as React.CSSProperties}
                >
                  <button
                    type="button"
                    className={`lib-book lib-book--orn${spine.ornament}${book.slug === activeSlug ? " lib-book--active" : ""}`}
                    title={book.title}
                    aria-label={book.title}
                    style={
                      {
                        "--spine-h": `${spine.height}px`,
                        "--spine-w": `${spine.width}px`,
                        "--spine-color": spine.color,
                        "--spine-font": `${spine.fontRem}rem`,
                      } as React.CSSProperties
                    }
                    onClick={() => onOpen(book)}
                  >
                    <span className="lib-book__spine">
                      <span className="lib-book__band lib-book__band--head" aria-hidden="true">
                        <span className="lib-book__glyph">{spine.glyph}</span>
                      </span>
                      <span className="lib-book__title">
                        {spine.words.map((word, index) => (
                          <span key={index} className="lib-book__word">{word}</span>
                        ))}
                      </span>
                      <span className="lib-book__band lib-book__band--foot" aria-hidden="true">
                        <span className="lib-book__glyph">{spine.glyph}</span>
                      </span>
                    </span>
                  </button>
                  {onSave && isSaved ? (
                    <SaveToRoomButton slug={book.slug} title={book.title} saved={isSaved(book.slug)} onToggle={onSave} variant="shelf" language={language} />
                  ) : null}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className={`shelf-arrow shelf-arrow--right${overflow.right ? "" : " shelf-arrow--off"}`}
            onClick={() => slide(1)}
            aria-label={t(language, "library.shelf.slideRight", { name: plate })}
            tabIndex={overflow.right ? 0 : -1}
          >
            <ArrowRight size={18} />
          </button>
        </div>
        {/* A real gold shelf plank: a plank FACE with depth + an ornamental finial cap
            on each end, so the shelf reads as an object, not a line. */}
        <div className="shelf-plank" aria-hidden="true">
          <span className="shelf-plank__finial shelf-plank__finial--l">❖</span>
          <span className="shelf-plank__finial shelf-plank__finial--r">❖</span>
        </div>
      </div>
    </section>
  );
}
